import { useEffect, useMemo, useState } from "react";
import { Col, Form } from "react-bootstrap";
import styled, { css } from "styled-components";
import { Controller, useFormContext } from "react-hook-form";
import ReactSelect from "react-select";

import { SelectChevronDown } from "../icons.jsx";
import { CustomFloatingLabel } from "./customfloatinglabel.jsx";
import { fontFamily } from "../grid.js";
import { useTranslation } from "react-i18next";

const FormSelectWrapper = styled("div").attrs({
  className: "FormSelectWrapper",
})`
  .react-select__control {
    padding: 0.5rem 10px;

    color: #333333;
    background-color: #f0f3f7;
    border-bottom: 1px solid #333333;
    border-radius: 4px 4px 0 0;

    font:
      400 13px/20px ${fontFamily},
      "Segoe UI",
      sans-serif;

    border-top: 0;
    border-left: 0;
    border-right: 0;
    background-size: 12px 6px;
    box-shadow: none;
    &:focus,
    &:hover {
      background-color: #f0f3f7;
      border-bottom: 1px solid #333333;
    }

    ${({ active }) =>
      active === "true" &&
      css`
        color: rgba(0, 58, 120, 1);
        background-color: rgba(230, 243, 254, 1);
      `};
  }
`;

export const FormSelect = ({
  name,
  label,
  options,
  formGroupProps,
  selectProps,
  required,
  disabled,
  customErrorMessage,
  additionalClassnames,
}) => {
  const { control } = useFormContext();
  const [searchValue, setSearchValue] = useState("");
  const isSearchValue = useMemo(() => searchValue.length > 0, [searchValue]);
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      render={({
        field: { value, onChange, disabled: globalDisabled, onBlur, ref },
        fieldState: { error, isDirty },
      }) => {
        const errorType = error?.type;

        useEffect(() => {
          if (value && typeof onChange === "function") {
            if (!isDirty) onChange({ value }); // manually trigger the onChange
          }
        }, [value, onChange]);

        const helperText = () => {
          if (errorType === "required" || errorType === undefined) {
            return t("FieldRequired", "Kenttä on pakollinen");
          }
          return errorType && customErrorMessage?.[errorType] ? customErrorMessage[errorType] : error?.message;
        };

        let selectedOption = "";
        if (options.length && value?.value) {
          // event.causeCode
          if (value.value.endsWith("P")) {
            // paid
            selectedOption = options[0].options.find((opt) => opt.value === value.value);
          } else if (value.value.endsWith("U")) {
            // unpaid
            selectedOption = options[1].options.find((opt) => opt.value === value.value);
          } else {
            // others
            selectedOption = options[2].options.find((opt) => opt.value === value.value);
          }
        }

        return (
          <Form.Group
            required={required}
            as={Col}
            xs={12}
            className={`d-flex flex-column mb-2 ${additionalClassnames ?? ""}`}
            controlId={`select-${name}`}
            {...formGroupProps}
          >
            <FormSelectWrapper active={(isDirty || isSearchValue).toString()}>
              <CustomFloatingLabel
                active={(isDirty || isSearchValue).toString()}
                label={`${label} ${required ? " *" : ""}`}
              >
                <ReactSelect
                  value={selectedOption}
                  onChange={onChange}
                  onInputChange={(newValue) => setSearchValue(newValue)}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  defaultOptions
                  ref={ref}
                  isDisabled={disabled || globalDisabled}
                  placeholder=""
                  noOptionsMessage={() => t("NoOptions", "Ei valintoja")}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={options}
                  onBlur={onBlur}
                  components={{
                    DropdownIndicator: SelectChevronDown,
                    IndicatorSeparator: null,
                    MultiValueRemove: null,
                  }}
                  {...selectProps}
                />
              </CustomFloatingLabel>
            </FormSelectWrapper>

            {error && (
              <Form.Control.Feedback type="invalid" className="d-block mb-1">
                {helperText()}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        );
      }}
    />
  );
};
